import { styled, type StyledDecorator } from '@glitz/react';
import * as React from 'react';
import { bareListDecorator } from 'webapp/decorators/list';
import { ArrowDown as ArrowDownIcon, CloseIcon } from 'webapp/components/icons';
import { SlideHeightAnimation, createFlipYDecorator, useOnClickOutside } from '@avensia/excite-ui';
import { createFieldSizeDecorator, FieldSize, type FieldSizeType } from 'webapp/components/field-size';
import { createThemeDecorator, Theme, type ThemeType } from 'webapp/theme';
import { createBorderDecorator } from 'webapp/components/border-decorator';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import { overflowYDecorator } from 'webapp/decorators/overflow';
import { Button } from 'webapp/components/button';

type PropType = {
  options: NordicNest.Api.SelectOptionApiModel[];
  value?: string;
  optionTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  selectedTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  selectedOptionDecorator?: StyledDecorator;
  as?: ThemeType;
  name?: string;
  onChangeOption?: (value: string) => void;
  size?: FieldSizeType;
  border?: boolean;
  placeholder?: string;
  selectedValueDecorator?: StyledDecorator;
  dropdownDecorator?: StyledDecorator;
  alignOptions?: 'left' | 'right';
  absolute?: boolean;
  boxShadowStyle?: string;
  noOptionsPadding?: boolean;
  onRemoveOption?: (option: NordicNest.Api.SelectOptionApiModel) => void;
  maxHeight?: string;
};

export default styled(function Select(props: PropType) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [value, setValue] = React.useState(props.value);
  const selectedOption = props.options?.find(o => o.value === value);
  const {
    onChangeOption,
    size = FieldSize.Default,
    as = Theme.White,
    border = false,
    selectedValueDecorator,
    selectedOptionDecorator,
    dropdownDecorator,
    noOptionsPadding,
    alignOptions,
    boxShadowStyle,
    absolute,
  } = props;

  React.useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const selectOption = React.useCallback(
    (value?: string) => {
      setValue(value);
      setIsOpen(false);
      if (value) {
        onChangeOption?.(value);
      }
    },
    [onChangeOption, setIsOpen, setValue],
  );

  const onClick = React.useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const selectValueDynamicStyle = React.useMemo(
    () =>
      selectedValueDecorator
        ? selectedValueDecorator
        : styled(createFieldSizeDecorator(size), createBorderDecorator(as, border)),
    [as, border, size, selectedValueDecorator],
  );

  const elementRef = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(elementRef, close, isOpen);

  const optionsDecorator = styled(
    alignOptions === 'left' && {
      left: 0,
      minWidth: 'max-content',
    },
    alignOptions === 'right' && {
      right: 0,
      minWidth: 'max-content',
    },
    alignOptions === undefined && {
      left: 0,
      right: 0,
    },
    {
      boxShadow: boxShadowStyle ?? 'rgba(0, 0, 0, 0.1) 0px 10px 40px 0px',
      maxHeight: props.maxHeight || '400px',
    },
  );

  return (
    <Base ref={elementRef}>
      <input type="hidden" name={props.name} value={value} />
      <SelectValue css={selectValueDynamicStyle} onClick={onClick}>
        <Value>
          {selectedOption && props.selectedTransform ? props.selectedTransform(selectedOption) : selectedOption?.text}
          {!selectedOption && <Placeholder>{props.placeholder}</Placeholder>}
        </Value>
        {props.onRemoveOption && selectedOption ? (
          <RemoveButton
            as={Theme.Transparent}
            disableRippleEffect
            onClick={e => {
              e.stopPropagation();
              if (selectedOption) {
                props.onRemoveOption?.(selectedOption);
              }
              selectOption(undefined);
            }}
          >
            <CloseIcon />
          </RemoveButton>
        ) : (
          <ArrowIcon css={createFlipYDecorator(isOpen)} />
        )}
      </SelectValue>
      <OptionsContainer>
        <Options
          css={styled(createThemeDecorator(as), optionsDecorator)}
          style={{ position: (absolute ?? true) ? 'absolute' : 'inherit' }}
          visible={isOpen}
        >
          <Dropdown
            role="listbox"
            css={styled(dropdownDecorator, {
              padding: { y: noOptionsPadding ? 0 : t => t.spacing.xs },
            })}
          >
            {props.options?.map((option, i) => (
              <Option
                css={styled(
                  createSelectDecorator(value === option.value),
                  value === option.value ? selectedOptionDecorator : undefined,
                  createFieldSizeDecorator(size, false, noOptionsPadding),
                  {
                    fontSize: 'inherit',
                  },
                )}
                onClick={() => selectOption(option.value)}
                role="option"
                aria-selected={value === option.value ? 'true' : 'false'}
                key={i}
              >
                {props.optionTransform ? props.optionTransform(option) : option.text}
              </Option>
            ))}
          </Dropdown>
        </Options>
      </OptionsContainer>
    </Base>
  );
});

const Base = styled.div({
  display: 'flex',
  flexDirection: 'column',
  fontSize: 'inherit',
});

function createSelectDecorator(selected: boolean) {
  return selected
    ? styled({
        fontWeight: 'bold',
      })
    : styled();
}

const Placeholder = styled.span({
  paddingLeft: t => t.spacing.xs,
  fontSize: t => t.spacing.s,
  fontWeight: t => t.fontWeight?.bold,
});

const SelectValue = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  zIndex: 1,
  height: '100%',
});

const textOverflow = styled({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
});

const Value = styled.span(textOverflow, {
  fontWeight: t => t.fontWeight?.bold,
  width: '100%',
});

const ArrowIcon = styled(ArrowDownIcon, {
  marginLeft: t => t.spacing.s,
  flexShrink: 0,
});

const Dropdown = styled.ul(bareListDecorator);

const OptionsContainer = styled.div({
  position: 'relative',
  zIndex: 2,
});

const Option = styled.li(textOverflow, {
  cursor: 'pointer',
});

const Options = styled(SlideHeightAnimation, scrollbarDecorator, overflowYDecorator, {
  top: 0,
  left: 0,
  maxHeight: '400px',
  boxShadow: 'rgba(0, 0, 0, 0.1) 10px 10px 40px 0px',
  width: '100%',
});

const RemoveButton = styled(Button, {
  padding: { xy: 0 },
});
